import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ApiError } from '../services/errors';
import { sessionKey } from '../hooks/use-auth';
import { AppLayout } from './AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { DashboardPage } from '../pages/DashboardPage';
import { ExportsPage } from '../pages/ExportsPage';
import { HistoryPage } from '../pages/HistoryPage';
import { LoginPage } from '../pages/LoginPage';
import { NewSearchPage } from '../pages/NewSearchPage';
import { SearchDetailPage } from '../pages/SearchDetailPage';

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    // Sessão expirada no meio do uso não pode deixar a tela quebrada
    // (RF-F03): o cache é invalidado e o roteador leva ao login.
    queryCache: new QueryCache({
      onError: (error) => {
        if (ApiError.is(error) && error.isUnauthenticated) {
          client.setQueryData(sessionKey, { user: null });
        }
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Não insiste em erro de contrato — só em falha transitória.
          if (ApiError.is(error) && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
        staleTime: 10_000,
      },
      mutations: { retry: false },
    },
  });

  return client;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/pesquisas/nova" element={<NewSearchPage />} />
        <Route path="/pesquisas/:searchId" element={<SearchDetailPage />} />
        <Route path="/historico" element={<HistoryPage />} />
        <Route path="/exportacoes" element={<ExportsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App({ client }: { client: QueryClient }) {
  return (
    <QueryClientProvider client={client}>
      <AppRoutes />
    </QueryClientProvider>
  );
}
